import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import AuthContext from "../../context/AuthContext";
import api from "../../api";
import WorkQueue from "./WorkQueue";
import PagedOptions from "./PagedOptions";
import ActivityHistory from "./ActivityHistory";
import AnswerFields from "./AnswerFields";
import PhotoEvidence from "./PhotoEvidence";
import ScenarioPreview from "./ScenarioPreview";
import SubmissionChecklist, { submissionCompleteness } from "./SubmissionChecklist";

const request = async (method, path, data) =>
  (await api({ method, url: `/surveys${path}`, data })).data;
const blankProject = {
  name: "",
  client_name: "",
  description: "",
  archived: false,
};
const newQuestion = (n) => ({
  id: `q${n}`,
  label: "",
  kind: "text",
  required: true,
  options: [],
});
const statusColor = (status) =>
  ({
    approved: "success",
    submitted: "info",
    returned: "warning",
    cancelled: "default",
  })[status] || "default";
const Status = ({ value }) => (
  <Chip
    size="small"
    color={statusColor(value)}
    label={value.replaceAll("_", " ")}
  />
);
const stamp = (value) =>
  value
    ? new Date(
        value.endsWith?.("Z") || /[+-]\d\d:\d\d$/.test(value)
          ? value
          : `${value}Z`,
      ).toLocaleString()
    : "—";

export default function Surveys() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const admin = Boolean(user?.is_superuser);
  const [projectId, setProjectId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [queueRevision, setQueueRevision] = useState(0);
  const [archived, setArchived] = useState(false);
  const [tab, setTab] = useState("assignments");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projectForm, setProjectForm] = useState(null);
  const [scenarioForm, setScenarioForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState(null);
  const [opened, setOpened] = useState(null);
  const [reportLoadId, setReportLoadId] = useState(0);
  const [answers, setAnswers] = useState({});
  const [dirty, setDirty] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState({});
  const photoPending = Object.values(pendingPhotos).some(Boolean);
  const [note, setNote] = useState("");

  const refreshSequence = useRef(0);
  const refresh = async (id = projectId) => {
    const sequence = ++refreshSequence.current;
    if (admin) {
      const nextDetail = id
        ? await request("get", `/projects/${id}?include_assignments=false&include_events=false`)
        : null;
      if (sequence !== refreshSequence.current) return;
      setDetail(nextDetail);
    }
    if (sequence === refreshSequence.current) setQueueRevision((n) => n + 1);
  };
  const run = async (operation, message) => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await operation();
      if (message) setSuccess(message);
    } catch (e) {
      const data = e.response?.data;
      const value = data?.detail || data?.errors || data?.message;
      setError(
        typeof value === "string"
          ? value
          : value
            ? JSON.stringify(value)
            : "Unable to complete this request. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    let active = true;
    setLoading(true);
    refresh()
      .catch(() => {
        if (active)
          setError("Unable to load the survey workspace. Please refresh.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      refreshSequence.current += 1;
    };
  }, [projectId, archived, admin]);
  const selectProject = (id) => {
    setDetail(null);
    setProjectId(id);
    setTab("assignments");
  };
  const openAssignment = async (id) => {
    const data = await request("get", `/assignments/${id}`);
    setOpened(data);
    setReportLoadId((value) => value + 1);
    setAnswers(data.assignment.answers);
    setPendingPhotos({});
    setNote("");
    setDirty(false);
  };
  const saveProject = () =>
    run(async () => {
      const { id, ...data } = projectForm;
      const result = await request(
        id ? "put" : "post",
        id ? `/projects/${id}` : "/projects",
        data,
      );
      setProjectForm(null);
      setProjectId(result.id);
      await refresh(result.id);
    }, "Project saved.");
  const saveScenario = () =>
    run(async () => {
      const { id, ...data } = scenarioForm;
      await request(
        id ? "put" : "post",
        id ? `/scenarios/${id}` : `/projects/${projectId}/scenarios`,
        data,
      );
      setScenarioForm(null);
      await refresh();
    }, "Scenario draft saved. Publish when ready to assign.");
  const saveAssignment = () =>
    run(async () => {
      const { shopper_label, ...data } = assignmentForm;
      await request("post", `/projects/${projectId}/assignments`, {
        ...data,
        due_date: new Date(assignmentForm.due_date).toISOString(),
      });
      setAssignmentForm(null);
      await refresh();
    }, "Assignment created. The shopper can find it in My work.");
  const action = (type) =>
    run(async () => {
      await request("post", `/assignments/${opened.assignment.id}/actions`, {
        action: type,
        expected_revision: opened.assignment.revision,
        note,
      });
      await openAssignment(opened.assignment.id);
      await refresh();
    }, "Assignment updated.");
  const saveAnswers = () =>
    run(async () => {
      const result = await request(
        "put",
        `/assignments/${opened.assignment.id}/answers`,
        { answers, expected_revision: opened.assignment.revision },
      );
      setOpened({ ...opened, assignment: result });
      setDirty(false);
      await refresh();
    }, "Draft saved to the server.");
  const editable =
    opened &&
    opened.assignment.shopper_id === user?.id &&
    ["in_progress", "returned"].includes(opened.assignment.status);
  const completeness = opened
    ? submissionCompleteness(opened.scenario.questions, answers, opened.assignment.evidence)
    : null;
  const scenarioEditor = (scenario, fork = false) =>
    setScenarioForm({
      ...(fork || !scenario.id ? {} : { id: scenario.id }),
      title: scenario.title || "",
      instructions: scenario.instructions || "",
      questions: scenario.questions || [newQuestion(1)],
    });
  const changeQuestion = (index, key, value) =>
    setScenarioForm({
      ...scenarioForm,
      questions: scenarioForm.questions.map((q, i) =>
        i === index
          ? {
              ...q,
              [key]: value,
              ...(key === "kind"
                ? { options: value === "choice" ? ["Yes", "No"] : [] }
                : {}),
            }
          : q,
      ),
    });
  const closeAssignment = () => {
    if (busy) return;
    if (
      (!dirty && !photoPending) ||
      window.confirm("Discard your unsaved changes and local photo selections?")
    )
      setOpened(null);
  };
  const reloadAssignment = () => {
    if (busy) return;
    if (
      (dirty || photoPending || note.trim()) &&
      !window.confirm(
        "Reload the saved report? This discards unsaved answers, review notes and local photo selections. Confirmed photos remain saved.",
      )
    ) return;
    run(
      () => openAssignment(opened.assignment.id),
      "Latest saved report loaded.",
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#f3f6fa", color: "#18324b" }}>
      <Header />
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        <Box
          component="aside"
          sx={{
            width: { md: 260 },
            p: 3,
            background: "#103e6b",
            color: "#fff",
            minHeight: { md: "calc(100vh - 64px)" },
            "& .MuiButton-root": {
              backgroundColor: "transparent",
              color: "#fff",
              borderColor: "#7995af",
              "&:hover": { backgroundColor: "#276294", color: "#fff" },
            },
          }}
        >
          <Typography variant="overline" sx={{ color: "#a9cfea" }}>
            Vancouver workspace
          </Typography>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Survey Manager
          </Typography>
          <Button
            fullWidth
            sx={{ color: "white", justifyContent: "flex-start", mb: 2 }}
            onClick={() => {
              setTab("mine");
            }}
          >
            My work
          </Button>
          {admin && (
            <>
              <Divider sx={{ borderColor: "#ffffff30", mb: 2 }} />
              <Typography variant="overline" display="block">
                Projects
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={archived}
                    onChange={(e) => {
                      setArchived(e.target.checked);
                      setProjectId(null);
                    }}
                  />
                }
                label="Archived"
              />
              <PagedOptions
                key={String(archived)}
                path={`project-options?archived=${archived}`}
                label="Projects"
                revision={queueRevision}
                selectedId={projectId}
                onSelect={(project) => selectProject(project.id)}
              />
              <Button
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => setProjectForm({ ...blankProject })}
              >
                + New project
              </Button>
            </>
          )}
          <Button
            fullWidth
            sx={{ mt: 4, color: "#c8e2f5" }}
            onClick={() => navigate("/")}
          >
            Back to dealer price lists
          </Button>
        </Box>
        <Box
          component="main"
          sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 4 }, maxWidth: 1500 }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Box>
              <Typography variant="overline" color="text.secondary">
                FIELDWORK / PILOT WORKSPACE
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {tab === "mine" || !admin
                  ? "My assignments"
                  : detail?.project.name || "Build your next fieldwork project"}
              </Typography>
            </Box>
            <Chip label="Pilot" color="info" variant="outlined" />
          </Stack>
          <Typography sx={{ mt: 1, mb: 3 }} color="text.secondary">
            {detail && tab !== "mine"
              ? `${detail.project.client_name} · ${detail.project.description || "Configure, assign and review in one place."}`
              : "Clear instructions, saved responses and a visible review status."}
          </Typography>
          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              onClose={() => setSuccess("")}
              sx={{ mb: 2 }}
            >
              {success}
            </Alert>
          )}
          {busy && (
            <Typography role="status" sx={{ mb: 1 }}>
              Saving…
            </Typography>
          )}
          {loading ? (
            <CircularProgress aria-label="Loading surveys" />
          ) : (
            <>
              {admin && detail && tab !== "mine" && (
                <>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    {[
                      ["Assignments", detail.summary.total],
                      [
                        "In field",
                        (detail.summary.statuses.assigned || 0) +
                          (detail.summary.statuses.in_progress || 0) +
                          (detail.summary.statuses.returned || 0),
                      ],
                      [
                        "Awaiting review",
                        detail.summary.statuses.submitted || 0,
                      ],
                      ["Approved", detail.summary.statuses.approved || 0],
                    ].map(([label, count]) => (
                      <Card key={label} sx={{ flex: 1 }}>
                        <CardContent>
                          <Typography variant="h4">{count}</Typography>
                          <Typography color="text.secondary">
                            {label}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Tabs
                      value={tab}
                      onChange={(_, v) => setTab(v)}
                      variant="scrollable"
                    >
                      <Tab label="Assignments" value="assignments" />
                      <Tab label="Scenarios" value="scenarios" />
                      <Tab label="Activity" value="activity" />
                    </Tabs>
                    <Button
                      onClick={() =>
                        setProjectForm({
                          id: detail.project.id,
                          name: detail.project.name,
                          client_name: detail.project.client_name,
                          description: detail.project.description,
                          archived: detail.project.archived,
                        })
                      }
                    >
                      Project settings
                    </Button>
                  </Stack>
                  {detail.project.archived && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Archived: existing assignments remain available; new work
                      is disabled.
                    </Alert>
                  )}
                </>
              )}
              {admin && !detail && tab !== "mine" && (
                <Card sx={{ p: 4 }}>
                  <Typography variant="h5">Start with a project</Typography>
                  <Typography sx={{ my: 2 }}>
                    Create a project, define and publish its first scenario,
                    then assign work to an existing active user.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setProjectForm({ ...blankProject })}
                  >
                    Create project
                  </Button>
                </Card>
              )}
              {(tab === "mine" ||
                !admin ||
                (detail && tab === "assignments")) && (
                <WorkQueue
                  key={admin && tab !== "mine" ? projectId : "mine"}
                  projectId={admin && tab !== "mine" ? projectId : null}
                  revision={queueRevision}
                  busy={busy}
                  onOpen={(id) => run(() => openAssignment(id))}
                  assignButton={
                    admin &&
                    tab !== "mine" && (
                      <Button
                        variant="contained"
                        disabled={
                          detail.project.archived ||
                          !detail.scenarios.some((s) => s.published)
                        }
                        onClick={() =>
                          setAssignmentForm({
                            scenario_id:
                              detail.scenarios.find((s) => s.published)?.id ||
                              "",
                            shopper_id: "",
                            location: "",
                            due_date: "",
                          })
                        }
                      >
                        + Assign work
                      </Button>
                    )
                  }
                />
              )}
              {admin && detail && tab === "scenarios" && (
                <>
                  <Button
                    variant="contained"
                    disabled={detail.project.archived}
                    sx={{ mb: 2 }}
                    onClick={() => scenarioEditor({})}
                  >
                    + New scenario version
                  </Button>
                  <Stack spacing={2}>
                    {detail.scenarios.map((s) => (
                      <Card key={s.id}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between">
                            <Box>
                              <Typography variant="h6">{s.title}</Typography>
                              <Typography color="text.secondary">
                                Version {s.version} · {s.questions.length}{" "}
                                questions
                              </Typography>
                            </Box>
                            <Chip
                              label={
                                s.published ? "Published · immutable" : "Draft"
                              }
                              color={s.published ? "success" : "default"}
                            />
                          </Stack>
                          <Typography sx={{ my: 2, whiteSpace: "pre-wrap" }}>
                            {s.instructions}
                          </Typography>
                          <Stack
                            direction="row"
                            gap={1}
                            sx={{ flexWrap: "wrap" }}
                          >
                            <Button
                              disabled={busy}
                              onClick={() => setPreview(s)}
                            >
                              Preview
                            </Button>
                            {!s.published && (
                              <>
                                <Button
                                  disabled={busy || detail.project.archived}
                                  onClick={() => scenarioEditor(s)}
                                >
                                  Edit draft
                                </Button>
                                <Button
                                  disabled={busy || detail.project.archived}
                                  onClick={() =>
                                    run(async () => {
                                      await request(
                                        "post",
                                        `/scenarios/${s.id}/publish`,
                                      );
                                      await refresh();
                                    }, "Scenario published. Assignments will pin this version.")
                                  }
                                >
                                  Publish
                                </Button>
                              </>
                            )}
                            <Button
                              disabled={detail.project.archived}
                              onClick={() => scenarioEditor(s, true)}
                            >
                              Copy into new version
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                    {!detail.scenarios.length && (
                      <Alert severity="info">
                        Add a scenario with instructions and questions, then
                        publish it before assigning work.
                      </Alert>
                    )}
                  </Stack>
                </>
              )}
              {admin && detail && tab === "activity" && (
                <ActivityHistory key={projectId} projectId={projectId} />
              )}
            </>
          )}
        </Box>
      </Box>

      <Dialog
        open={Boolean(projectForm)}
        onClose={() => !busy && setProjectForm(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {projectForm?.id ? "Project settings" : "Create project"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={3} sx={{ pt: 1 }}>
            {["name", "client_name", "description"].map((key) => (
              <TextField
                key={key}
                label={
                  {
                    name: "Project name",
                    client_name: "Client name",
                    description: "Description",
                  }[key]
                }
                value={projectForm?.[key] || ""}
                onChange={(e) =>
                  setProjectForm({ ...projectForm, [key]: e.target.value })
                }
                multiline={key === "description"}
                rows={key === "description" ? 3 : 1}
                inputProps={{ maxLength: key === "description" ? 5000 : 160 }}
              />
            ))}
            {projectForm?.id && (
              <FormControlLabel
                control={
                  <Switch
                    checked={projectForm.archived}
                    onChange={(e) =>
                      setProjectForm({
                        ...projectForm,
                        archived: e.target.checked,
                      })
                    }
                  />
                }
                label="Archive project"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setProjectForm(null)}>
            Cancel
          </Button>
          <Button
            disabled={
              busy ||
              !projectForm?.name.trim() ||
              !projectForm?.client_name.trim()
            }
            onClick={saveProject}
          >
            Save project
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(scenarioForm)}
        onClose={() => !busy && setScenarioForm(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {scenarioForm?.id ? "Edit scenario draft" : "New scenario version"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              label="Scenario title"
              value={scenarioForm?.title || ""}
              onChange={(e) =>
                setScenarioForm({ ...scenarioForm, title: e.target.value })
              }
              inputProps={{ maxLength: 160 }}
            />
            <TextField
              label="Shopper instructions"
              multiline
              rows={3}
              value={scenarioForm?.instructions || ""}
              onChange={(e) =>
                setScenarioForm({
                  ...scenarioForm,
                  instructions: e.target.value,
                })
              }
            />
            {scenarioForm?.questions.map((q, i) => (
              <Card variant="outlined" key={i}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography>
                        Question {i + 1} · {q.id}
                      </Typography>
                      <Button
                        disabled={scenarioForm.questions.length === 1}
                        onClick={() =>
                          setScenarioForm({
                            ...scenarioForm,
                            questions: scenarioForm.questions.filter(
                              (_, n) => n !== i,
                            ),
                          })
                        }
                      >
                        Remove
                      </Button>
                    </Stack>
                    <TextField
                      label="Question"
                      value={q.label}
                      onChange={(e) =>
                        changeQuestion(i, "label", e.target.value)
                      }
                    />
                    <TextField
                      select
                      label="Answer type"
                      value={q.kind}
                      onChange={(e) =>
                        changeQuestion(i, "kind", e.target.value)
                      }
                    >
                      {["text", "number", "choice"].map((k) => (
                        <MenuItem key={k} value={k}>
                          {k}
                        </MenuItem>
                      ))}
                    </TextField>
                    {q.kind === "choice" && (
                      <TextField
                        label="Options (one per line)"
                        multiline
                        rows={3}
                        value={q.options.join("\n")}
                        onChange={(e) =>
                          changeQuestion(
                            i,
                            "options",
                            e.target.value.split("\n"),
                          )
                        }
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={q.required}
                          onChange={(e) =>
                            changeQuestion(i, "required", e.target.checked)
                          }
                        />
                      }
                      label="Answer required"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!q.photo_required}
                          onChange={(e) =>
                            changeQuestion(
                              i,
                              "photo_required",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label="Photo required"
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button
              disabled={(scenarioForm?.questions.length || 0) >= 40}
              onClick={() => {
                let n = 1;
                while (scenarioForm.questions.some((q) => q.id === `q${n}`))
                  n++;
                setScenarioForm({
                  ...scenarioForm,
                  questions: [...scenarioForm.questions, newQuestion(n)],
                });
              }}
            >
              + Add question
            </Button>
            <Alert severity="info">
              Publishing locks this version. Later edits create a new version;
              accepted assignments keep their original questions. Pilot supports
              text, numbers and single choice.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setScenarioForm(null)}>
            Cancel
          </Button>
          <Button
            disabled={
              busy ||
              !scenarioForm?.title.trim() ||
              scenarioForm?.questions.some((q) => !q.label.trim())
            }
            onClick={() => setPreview(scenarioForm)}
          >
            Preview draft
          </Button>
          <Button
            disabled={
              busy ||
              !scenarioForm?.title.trim() ||
              scenarioForm?.questions.some((q) => !q.label.trim())
            }
            onClick={saveScenario}
          >
            Save draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(assignmentForm)}
        onClose={() => !busy && setAssignmentForm(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign fieldwork</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              select
              label="Published scenario"
              value={assignmentForm?.scenario_id || ""}
              onChange={(e) =>
                setAssignmentForm({
                  ...assignmentForm,
                  scenario_id: Number(e.target.value),
                })
              }
            >
              {detail?.scenarios
                .filter((s) => s.published)
                .map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.title} · v{s.version}
                  </MenuItem>
                ))}
            </TextField>
            {assignmentForm && <>
              <Typography>
                Selected shopper: {assignmentForm.shopper_label || "None"}
              </Typography>
              <PagedOptions path="shopper-options" label="Active shoppers"
                selectedId={assignmentForm.shopper_id}
                onSelect={(person) => setAssignmentForm({
                  ...assignmentForm, shopper_id: person.id,
                  shopper_label: `${person.name} · ${person.email}`,
                })} />
            </>}
            <TextField
              label="Location / task"
              value={assignmentForm?.location || ""}
              onChange={(e) =>
                setAssignmentForm({
                  ...assignmentForm,
                  location: e.target.value,
                })
              }
            />
            <TextField
              type="datetime-local"
              label="Due (your local time)"
              InputLabelProps={{ shrink: true }}
              value={assignmentForm?.due_date || ""}
              onChange={(e) =>
                setAssignmentForm({
                  ...assignmentForm,
                  due_date: e.target.value,
                })
              }
            />
            <Alert severity="info">
              Assignment creation does not send a notification or promise
              payment. Confirm pilot arrangements with the shopper separately.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setAssignmentForm(null)}>
            Cancel
          </Button>
          <Button
            disabled={
              busy ||
              !assignmentForm?.shopper_id ||
              !assignmentForm?.location.trim() ||
              !assignmentForm?.due_date
            }
            onClick={saveAssignment}
          >
            Create assignment
          </Button>
        </DialogActions>
      </Dialog>

      {preview && (
        <ScenarioPreview scenario={preview} onClose={() => setPreview(null)} />
      )}
      <Drawer
        anchor="right"
        open={Boolean(opened)}
        onClose={closeAssignment}
        PaperProps={{
          sx: { width: 580, maxWidth: "100vw", boxSizing: "border-box", p: 3 },
        }}
      >
        {opened && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5">SM-{opened.assignment.id}</Typography>
              <Button onClick={closeAssignment}>Close</Button>
            </Stack>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {opened.assignment.location}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ my: 2 }}>
              <Status value={opened.assignment.status} />
              <Chip
                size="small"
                label={`Scenario v${opened.scenario.version}`}
              />
            </Stack>
            <Typography variant="body2">
              Due {stamp(opened.assignment.due_date)}
            </Typography>
            <Typography sx={{ my: 3, whiteSpace: "pre-wrap" }}>
              {opened.scenario.instructions}
            </Typography>
            {opened.assignment.review_note && (
              <Alert
                severity={
                  opened.assignment.status === "returned" ? "warning" : "info"
                }
                sx={{ mb: 2 }}
              >
                {opened.assignment.review_note}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <AnswerFields
              renderEvidence={(q) => (
                <PhotoEvidence
                  key={`${opened.assignment.id}-${reportLoadId}-${q.id}`}
                  question={q}
                  assignment={opened.assignment}
                  photos={opened.photos || []}
                  editable={editable}
                  busy={busy}
                  onBusy={setBusy}
                  onPending={(value) =>
                    setPendingPhotos((current) => ({
                      ...current,
                      [q.id]: value,
                    }))
                  }
                  onConfirmed={({ assignment, photo }) =>
                    setOpened((current) => ({
                      ...current,
                      assignment,
                      photos: [
                        ...(current.photos || []).filter(
                          (p) => p.uuid !== photo.uuid,
                        ),
                        photo,
                      ],
                    }))
                  }
                />
              )}
              questions={opened.scenario.questions}
              answers={answers}
              disabled={!editable || busy}
              onChange={(value) => {
                setAnswers(value);
                setDirty(true);
              }}
            />
            <Divider sx={{ my: 3 }} />
            <Typography variant="caption" sx={{ mb: 2 }}>
              {dirty
                ? "Unsaved changes on this screen."
                : "Showing the latest loaded server revision."}{" "}
              Offline recovery is not available in this pilot.
            </Typography>
            <Button disabled={busy} onClick={reloadAssignment} sx={{ mb: 2 }}>
              Reload saved report
            </Button>
            <Typography variant="caption" sx={{ mb: 2 }}>
              If another session changed this report, reload its saved version
              before making further changes.
            </Typography>
            {opened.assignment.shopper_id === user?.id &&
              opened.assignment.status === "assigned" && (
                <Button
                  variant="contained"
                  disabled={busy}
                  onClick={() => action("accept")}
                >
                  Accept assignment
                </Button>
              )}
            {editable && (
              <SubmissionChecklist
                completeness={completeness}
                dirty={dirty}
                photoPending={photoPending}
              />
            )}
            {editable && (
              <Stack direction="row" spacing={1}>
                <Button disabled={busy || !dirty} onClick={saveAnswers}>
                  Save draft
                </Button>
                <Button
                  variant="contained"
                  disabled={busy || dirty || photoPending || !completeness.complete}
                  onClick={() => action("submit")}
                >
                  Submit for review
                </Button>
              </Stack>
            )}
            {admin &&
              ["assigned", "in_progress", "submitted", "returned"].includes(
                opened.assignment.status,
              ) && (
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    label="Review / cancellation note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    inputProps={{ maxLength: 2000 }}
                  />
                  <Stack
                    direction="row"
                    gap={1}
                    sx={{ mt: 2, flexWrap: "wrap" }}
                  >
                    {opened.assignment.status === "submitted" && (
                      <>
                        <Button
                          disabled={busy || !note.trim()}
                          onClick={() => action("return")}
                        >
                          Return for correction
                        </Button>
                        <Button
                          disabled={busy}
                          onClick={() => action("approve")}
                        >
                          Approve QC
                        </Button>
                      </>
                    )}
                    <Button
                      color="error"
                      disabled={busy || !note.trim()}
                      onClick={() => {
                        if (
                          window.confirm(
                            "Cancel this assignment? Expense obligations must be handled separately.",
                          )
                        )
                          action("cancel");
                      }}
                    >
                      Cancel assignment
                    </Button>
                  </Stack>
                </Box>
              )}
          </>
        )}
      </Drawer>
    </Box>
  );
}
